import {
  faChevronUp,
  faMinus,
  faPipe,
  faThumbsUp,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, IconButton, Text, useTooltip } from '@modusign/design-system';
import { Dispatch, SetStateAction, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FOOTER, GNB } from '@app/constants/dimension';
import {
  Breakpoint,
  ButtonBox,
  getIsMinimized,
  MousePositionTrackingTooltip,
  toggleDisplay,
} from '@app/features/formRecommendation';
import useMouseClientPosition from '@app/hooks/useMouseClientPosition';

const CONSTRAINT = {
  CHANNELTALK_OFFSET: 154,
  GNB: {
    OFFSET: 36,
    WIDTH: GNB.DESKTOP.WIDTH,
  },
  SHEET: {
    CLOSED_HEIGHT: 56,
    CLOSED_PADDING: 36,
    HEADER_HEIGHT: 22,
    MAX_HEIGHT: 234,
    OPENED_PADDING: 42,
  },
  FOOTER_HEIGHT: FOOTER.DESKTOP.HEIGHT,
} as const;

interface DesktopFormRecommendationSheetProps {
  breakpoint: Breakpoint;
  formIds: string[];
  isOpenHandler: Dispatch<SetStateAction<boolean | undefined>>;
}

function DesktopFormRecommendationSheet({
  breakpoint,
  formIds,
  isOpenHandler,
}: DesktopFormRecommendationSheetProps) {
  const dispatch = useDispatch();
  const isMinimized = useSelector(getIsMinimized);

  const { activated, triggerRef, position, top, popperRef } = useTooltip({});
  const trackerRef = useRef<HTMLDivElement>(null);
  const { clientX } = useMouseClientPosition(trackerRef);

  const buttonBoxRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Box
        bottom={CONSTRAINT.FOOTER_HEIGHT}
        left={CONSTRAINT.GNB.WIDTH}
        ml={9}
        p={0}
        position="fixed"
        zIndex={100}
      >
        <Flex
          alignItems="center"
          backgroundColor={isMinimized ? 'pureNavy50' : 'pureNavy25'}
          borderColor="boxBorderNeutralBase"
          borderRadius="6px 6px 0 0"
          borderStyle="solid"
          borderWidth="1px 1px 0 1px"
          flexDirection="column"
          height={
            isMinimized
              ? CONSTRAINT.SHEET.CLOSED_HEIGHT
              : CONSTRAINT.SHEET.HEADER_HEIGHT +
                CONSTRAINT.SHEET.OPENED_PADDING +
                (buttonBoxRef.current?.offsetHeight ?? 0)
          }
          justifyContent="center"
          maxHeight={CONSTRAINT.SHEET.MAX_HEIGHT}
          width={`calc(100vw - ${CONSTRAINT.GNB.WIDTH}px - ${CONSTRAINT.CHANNELTALK_OFFSET}px)`}
          {...(isMinimized
            ? {
                ref: triggerRef,
                sx: {
                  '&:hover': {
                    '& svg': {
                      color: 'iconNeutralDarkest',
                    },
                    '& .title, .description': {
                      color: 'textNeutralDarkest',
                    },
                    '& .badge': {
                      backgroundColor: 'statusBackgroundNeutralDarkest',
                    },
                  },
                  transition: 'height 0.1s ease-out',
                },
              }
            : {
                pb: 7,
                pt: 6,
                px: 7,
                sx: { transition: 'height 0.1s ease-out' },
              })}
        >
          <Flex
            alignItems="center"
            mt={isMinimized ? buttonBoxRef.current?.offsetHeight : undefined}
            width="100%"
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              {...(isMinimized
                ? {
                    px: 7,
                    py: 6,
                    sx: {
                      '&:hover': {
                        cursor: 'pointer',
                      },
                    },
                  }
                : {
                    ref: triggerRef,
                    sx: {
                      '&:hover': {
                        cursor: 'pointer',
                        '& .toggle-button': {
                          color: 'iconNeutralDarkest',
                        },
                      },
                    },
                  })}
              onClick={() => dispatch(toggleDisplay({ isMinimized: !isMinimized }))}
            >
              <Flex alignItems="center" justifyContent="start">
                <Flex color="iconNeutralSlightlyDark">
                  <FontAwesomeIcon height="11px" icon={faThumbsUp} width="11px" />
                </Flex>
                <Text
                  className="title"
                  color="textNeutralSlightlyLight"
                  fontSize="lg"
                  fontWeight="medium"
                  mx={1}
                >
                  추천 서식
                </Text>
                <Flex
                  alignItems="center"
                  backgroundColor="statusBackgroundNeutralUltraDark"
                  borderRadius="10px"
                  className="badge"
                  height="16px"
                  justifyContent="center"
                  px="2px"
                  width="30px"
                >
                  <Text color="textNeutralLightest" fontSize="xxs" fontWeight="medium">
                    무료
                  </Text>
                </Flex>
                <Text
                  className="description"
                  color="textNeutralSlightlyLight"
                  display={isMinimized ? 'block' : 'none'}
                  fontSize="sm"
                  ml={4}
                >
                  전문가가 직접 작성·검토한 서식을 편집하여 저장해 보세요
                </Text>
              </Flex>

              <Box>
                {isMinimized ? (
                  <IconButton color="iconNeutralBase">
                    <FontAwesomeIcon icon={faChevronUp} width="13px" />
                  </IconButton>
                ) : (
                  <IconButton color="iconNeutralBase">
                    <FontAwesomeIcon className="toggle-button" icon={faMinus} width="13px" />
                  </IconButton>
                )}
              </Box>
            </Flex>

            {!isMinimized && (
              <>
                <Box color="lightGray300" mx="20px">
                  <FontAwesomeIcon icon={faPipe} />
                </Box>
                <IconButton
                  color="iconNeutralBase"
                  sx={{
                    '&:hover': {
                      color: 'iconNeutralDarkest',
                    },
                    width: '9px',
                    height: '14px',
                  }}
                  onClick={() => isOpenHandler(false)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              </>
            )}
          </Flex>

          <Flex
            gap={3}
            maxHeight={
              CONSTRAINT.SHEET.MAX_HEIGHT -
              (CONSTRAINT.SHEET.OPENED_PADDING - CONSTRAINT.SHEET.CLOSED_PADDING) -
              CONSTRAINT.SHEET.CLOSED_HEIGHT
            }
            pt={4}
            ref={buttonBoxRef}
            sx={{
              visibility: isMinimized ? 'hidden' : 'visible',
            }}
            width="100%"
          >
            {formIds.map((formId) => (
              <ButtonBox breakpoint={breakpoint} formId={formId} key={formId} />
            ))}
          </Flex>
        </Flex>

        {activated && (
          <MousePositionTrackingTooltip
            left={clientX - CONSTRAINT.GNB.WIDTH - CONSTRAINT.GNB.OFFSET}
            position={position}
            ref={popperRef}
            top={isMinimized ? top + 5 : top}
          >
            {isMinimized ? '펼치기' : '접기'}
          </MousePositionTrackingTooltip>
        )}
      </Box>
      <Box ref={trackerRef} />
    </>
  );
}

export default DesktopFormRecommendationSheet;
